import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AssetsView } from './AssetsView'
import type { Asset } from '@/types/assets'

interface AssetPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: number
  projectId?: number
  projects?: Array<{ id: number; name: string }>
  onAssetSelect: (asset: Asset, sourceType: 'organization' | 'project', sourceId: number) => void
}

export function AssetPicker({
  open,
  onOpenChange,
  organizationId,
  projectId,
  projects = [],
  onAssetSelect
}: AssetPickerProps) {
  const { t } = useTranslation('assets')
  const [activeTab, setActiveTab] = useState<string>('organization')
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(projectId)

  const handleAssetSelect = (asset: Asset) => {
    if (activeTab === 'organization') {
      onAssetSelect(asset, 'organization', organizationId)
    } else if (selectedProjectId) {
      onAssetSelect(asset, 'project', selectedProjectId)
    }
    onOpenChange(false)
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    if (value === 'project' && !selectedProjectId && projects.length > 0) {
      setSelectedProjectId(projects[0].id)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('picker.title')}</DialogTitle>
          <DialogDescription>
            {t('picker.description')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="organization">{t('tabs.organization')}</TabsTrigger>
            <TabsTrigger value="project" disabled={!projectId && projects.length === 0}>
              {t('tabs.project')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organization" className="flex-1 min-h-0 mt-4">
            <div className="h-full border rounded-lg overflow-hidden">
              <AssetsView
                ownerType="organization"
                ownerId={organizationId}
                selectionMode={true}
                onAssetSelect={handleAssetSelect}
              />
            </div>
          </TabsContent>

          <TabsContent value="project" className="flex-1 min-h-0 mt-4">
            {selectedProjectId ? (
              <div className="h-full border rounded-lg overflow-hidden">
                <AssetsView
                  ownerType="project"
                  ownerId={selectedProjectId}
                  selectionMode={true}
                  onAssetSelect={handleAssetSelect}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                {t('picker.noProject')}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
